/* eslint-disable react-hooks/set-state-in-effect, react-hooks/refs -- Effect resets state when the panel closes; searchUsersRef latches the latest closure for use inside async event handlers. */
import { SearchIcon, ShieldIcon, UserIcon, XIcon } from 'lucide-react';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import type { AdminUserSearchResultDto } from '@splitfiesta/shared';

import { useShadowMode } from '@/hooks/useShadowMode';

import { extractData } from '@/utils/apiTypes';
import { toast } from '@/utils/toast';

interface ShadowUserPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ShadowUserPanel({ isOpen, onClose }: ShadowUserPanelProps) {
  const { t } = useTranslation('admin');
  const { enterShadowMode, searchUsers } = useShadowMode();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminUserSearchResultDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchUsersRef = useRef(searchUsers);
  searchUsersRef.current = searchUsers;

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setResults([]);
      setHasSearched(false);
    }
  }, [isOpen]);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setIsLoading(true);
    setHasSearched(true);
    try {
      const response = await searchUsersRef.current(query.trim());
      const data = extractData<AdminUserSearchResultDto[]>(response);
      setResults(data);
    } catch {
      toast.error(t('shadow.searchError'));
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [query, t]);

  const handleShadow = useCallback(
    async (userId: string) => {
      try {
        await enterShadowMode({ userId });
        onClose();
      } catch {
        toast.error(t('shadow.enterError'));
      }
    },
    [enterShadowMode, onClose, t]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        data-testid="shadow-panel-backdrop"
      />

      {/* Panel */}
      <div
        className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[70vh] flex flex-col"
        data-testid="shadow-user-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ShieldIcon className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('shadow.banner')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common:actions.close')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="email"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder={t('shadow.searchPlaceholder')}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              data-testid="shadow-search-input"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4 text-sm">
              {t('shadow.searchLoading')}
            </p>
          )}
          {!isLoading && hasSearched && results.length === 0 && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4 text-sm">
              {t('shadow.searchEmpty')}
            </p>
          )}
          {results.map(user => (
            <div
              key={user.id}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <div className="flex items-center gap-3 min-w-0">
                {user.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt={user.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                    <UserIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user.name}
                    </p>
                    {user.systemRole !== 'user' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400">
                        {user.systemRole}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user.email}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleShadow(user.id)}
                className="shrink-0 ml-2 px-3 py-1 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 rounded transition-colors"
                data-testid={`shadow-btn-${user.id}`}
              >
                {t('shadow.startButton')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
